import { Alert, Button, Card, Col, Form, Input, Row, Typography } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React from 'react';
import { KeyOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * Cambio de contraseña de OTRO usuario, desde /admin/usuarios.
 *
 * Vive aparte del formulario de edición a propósito: cambiarle la contraseña a alguien lo
 * deja afuera de su cuenta, así que no debería poder pasar sin querer mientras se corrige
 * un correo. Por eso también pide la contraseña de quien hace el cambio — el backend la
 * verifica (`PUT /user/:id/password`), no es un adorno del formulario.
 *
 * El formulario no envía nada si la confirmación no coincide: descubrirlo por un 422 del
 * servidor sería tarde, con el usuario ya sin acceso.
 */
function ChangeUserPasswordForm({ usuario, submitForm, onCancel, isSubmitting = false }) {
  const [form] = useForm();

  const finishForm = async ({ password, actorPassword }) => {
    const result = await submitForm({ password, actorPassword });
    if (result?.resetFields) form.resetFields();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    else form.resetFields();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <Title level={3} className="flex items-center justify-center mb-2">
          <KeyOutlined className="mr-2 text-blue-500" />
          Cambiar contraseña
        </Title>
        <Text type="secondary">
          Le vas a asignar una contraseña nueva a{' '}
          <strong>{usuario?.fullname || usuario?.rut}</strong>. Avísale, porque con la anterior no
          va a poder entrar.
        </Text>
      </div>

      <Card className="shadow-md">
        <Form form={form} onFinish={finishForm} layout="vertical" size="large">
          <Form.Item
            name="password"
            label={
              <span className="flex items-center">
                <LockOutlined className="mr-1" />
                Contraseña nueva
              </span>
            }
            rules={[
              { required: true, message: 'Ingresa la contraseña nueva' },
              { min: 8, message: 'La contraseña debe tener al menos 8 caracteres' },
            ]}
          >
            <Input.Password placeholder="Contraseña nueva" autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="repeatPassword"
            label={
              <span className="flex items-center">
                <LockOutlined className="mr-1" />
                Confirmar contraseña nueva
              </span>
            }
            dependencies={['password']}
            rules={[
              { required: true, message: 'Confirma la contraseña nueva' },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || getFieldValue('password') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('Las contraseñas no coinciden')),
              }),
            ]}
          >
            <Input.Password placeholder="Repetir contraseña nueva" autoComplete="new-password" />
          </Form.Item>

          <Alert
            message="Confirma que eres tú"
            description="Para cambiar la contraseña de otra persona necesitas ingresar la tuya."
            type="info"
            showIcon
            className="mb-4"
          />

          <Form.Item
            name="actorPassword"
            label={
              <span className="flex items-center">
                <SafetyCertificateOutlined className="mr-1" />
                Tu contraseña
              </span>
            }
            rules={[{ required: true, message: 'Ingresa tu contraseña' }]}
          >
            <Input.Password placeholder="Tu contraseña actual" autoComplete="current-password" />
          </Form.Item>

          <Form.Item className="mb-0">
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12}>
                <Button
                  block
                  size="large"
                  className="h-12"
                  disabled={isSubmitting}
                  onClick={handleCancel}
                >
                  Cancelar
                </Button>
              </Col>
              <Col xs={24} sm={12}>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  size="large"
                  icon={<KeyOutlined />}
                  className="h-12"
                >
                  {isSubmitting ? 'Cambiando...' : 'Cambiar contraseña'}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default ChangeUserPasswordForm;
